import React from 'react'
import { uniq, countBy, isNil, round } from 'lodash'
import Select from 'common/components/form/Select'
import { Button, Grid, IconButton, SvgIcon } from '@mui/material'
import { CardTable, SORT } from 'common/components/datatable/Table'
import PropTypes from 'prop-types'
import Input from 'common/components/form/Input'
import { getFullDate } from 'common/utils/DateUtil'
import moment from 'moment'
import JenkinsAction from 'common/actions/JenkinsAction'
import ApplicationConf from 'conf/ApplicationConf'
import { aquaFetch } from 'common/utils/ActionUtils'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router'

const TABLE_HEADERS = [
    { key: 'instance', value: 'Instance' },
    { key: 'service', value: 'Service' },
    { key: 'directory', value: 'Répertoire' },
    { key: 'server', value: 'Serveur' },
    { key: 'version', value: 'Version' },
    { key: 'commit', value: 'Commit' },
    { key: 'branch', value: 'Branche' },
    { key: 'deploy', value: 'Deploy' },
    { key: 'launch', value: 'Launch' },
    { key: 'inUseMemory', value: 'RAM utilisée' },
    { key: 'totalMemory', value: 'Allouée' },
    { key: 'maxMemory', value: 'Max' },
    { key: 'nbProcess', value: 'NB Process' },
]

const EqualIcon = () => (
    <SvgIcon style={{ fontSize: '26px', color: '#666666' }}>
        <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'>
            <path fill='currentColor' d='M18 9.998H6a1 1 0 1 1 0-2h12a1 1 0 0 1 0 2zm0 6H6a1 1 0 0 1 0-2h12a1 1 0 0 1 0 2z' />
        </svg>
    </SvgIcon>
)

const getDateColor = (date) => {
    if (!date) {
        return { color: '#fd7d26', tooltip: 'Info manquante' }
    }
    if (moment().subtract(2, 'minutes').valueOf() < moment(date).valueOf()) {
        return { color: '#85ffab', tooltip: '< 2 minutes' }
    }
    if (moment().subtract(5, 'minutes').valueOf() < moment(date).valueOf()) {
        return { color: '#04ff51', tooltip: '< 5 minutes' }
    }
    if (moment().subtract(10, 'minutes').valueOf() < moment(date).valueOf()) {
        return { color: '#00972d', tooltip: '< 10 minutes' }
    }
    if (moment().subtract(20, 'minutes').valueOf() < moment(date).valueOf()) {
        return { color: '#29abff', tooltip: '< 20 minutes' }
    }
    if (moment().subtract(30, 'minutes').valueOf() < moment(date).valueOf()) {
        return { color: '#6330ff', tooltip: '< 30 minutes' }
    }
    if (moment().subtract(1, 'hour').valueOf() < moment(date).valueOf()) {
        return { color: '#cb77ff', tooltip: '< 1 heure' }
    }
    if (moment().subtract(2, 'hour').valueOf() < moment(date).valueOf()) {
        return { color: '#ff94f5', tooltip: '< 2 heures' }
    }
    return undefined
}

const NotEqualIcon = () => (
    <SvgIcon style={{ fontSize: '26px', color: '#666666' }}>
        <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'>
            <path fill='currentColor' d='M18 9.998H6a1 1 0 1 1 0-2h12a1 1 0 0 1 0 2zm0 6H6a1 1 0 0 1 0-2h12a1 1 0 0 1 0 2z' />
            <path fill='currentColor' d='M14.999 5H15c.507.219.742.806.525 1.314l-5.212 12.162A.999.999 0 0 1 9 19a1.002 1.002 0 0 1-.525-1.314l5.212-12.162A.999.999 0 0 1 15 5z' />
        </svg>
    </SvgIcon>
)


const KwakeUpTable = ({
    results = [],
    filter = {},
    setFilter = () => { },
}) => {
    const navigate = useNavigate()

    const {
        datacenter,
        branch,
        instance,
        service,
        version,
        versionIsEqual,
    } = filter

    const filteredByInstance = instance ? results.filter(r => r.instance.name === instance) : results
    const filteredByServer = datacenter ? filteredByInstance.filter(r => r.instance.server === datacenter) : filteredByInstance
    const filteredByBranch = branch ? filteredByServer.filter(r => r.branch === branch) : filteredByServer
    const filteredByService = service ? filteredByBranch.filter(r => r.service.name === service) : filteredByBranch
    const filteredByVersion = version ? filteredByService.filter(r => versionIsEqual ? r.number === version : r.number !== version) : filteredByService

    const listIcon = {
        icon: 'filter_list',
        color: '#35609f',
    }

    const tableData = filteredByVersion.map(r => {
        return {
            instance: {
                value: r.instance.name,
                color: r.isRunning ? '#85ffab' : '#ff8e8e',
                leftIcon: listIcon,
                onClick: () => setFilter(f => ({ ...f, instance: r.instance.name })),
                url: r.instance.url,
                id: r.instance.id,
            },
            service: {
                id: r.service.id,
                value: r.service.name,
                color: r.isRunning ? '#85ffab' : '#ff8e8e',
                leftIcon: listIcon,
                onClick: () => setFilter(f => ({ ...f, service: r.service.name })),
                gitlabName: r.service.gitlabName,
            },
            directory: {
                value: r.directory,
                color: !r.directory ? '#fd7d26' : undefined,
            },
            server: {
                value: r.instance.server ? `dt${r.instance.server}` : undefined,
                leftIcon: listIcon,
                onClick: () => setFilter(f => ({ ...f, datacenter: r.instance.server })),
            },
            version: {
                value: r.number,
                leftIcon: listIcon,
                onClick: () => setFilter(f => ({ ...f, version: r.number, versionIsEqual: true })),
            },
            commit: {
                value: `${r.commitDate || ''} ${r.lastCommit ? `[${r.lastCommit.substring(0, 10)}]` : '[???]'}`,
                onClick: r.lastCommit ? () => window.open(`https://gitlab2.aquasys.fr/sieau/${r.service.gitlabName}/-/commit/${r.lastCommit}`, '_blank') : undefined,
                color: !r.lastCommit || !r.commitDate ? '#fd7d26' : undefined,
                fontColor: '#35609f',
            },
            branch: {
                value: r.branch,
                color: !r.branch ? '#fd7d26' : undefined,
                leftIcon: r.branch ? listIcon : undefined,
                onClick: () => setFilter(f => ({ ...f, branch: r.branch })),
            },
            deploy: { value: getFullDate(r.deployDate), ...getDateColor(r.deployDate) },
            launch: { value: getFullDate(r.launchDate), ...getDateColor(r.launchDate) },
            inUseMemory: {
                value: r.inUseMemory ? `${round(r.inUseMemory / (1024*1024))} (${round(r.inUseMemory * 100 / r.maxMemory)}%)` : undefined,
                sortValue: r.inUseMemory ? round(r.inUseMemory * 100 / r.maxMemory) : undefined,
                color: r.inUseMemory && round(r.inUseMemory * 100 / r.maxMemory) >= 90 ? '#ff8e8e' : undefined,
            },
            totalMemory: { value: r.totalMemory ? round(r.totalMemory / (1024*1024)) : undefined },
            maxMemory: { value: r.maxMemory ? round(r.maxMemory / (1024*1024)) : undefined },
            nbProcess: { value: r.nbProcess },
            url: r.instance.url,
            isProblem: !r.isRunning,
            isIncomplete: [r.lastCommit, r.commitDate, r.branch, r.deployDate, r.launchDate, r.memory].some(e => isNil(e)),
        }
    })

    const lineActions = [
        {
            icon: 'query_stats',
            color: '#35609f',
            onClick: e => navigate(`/instance/${e.instance.id}/service/${e.service.id}`),
        }, {
            icon: 'play_circle_filled',
            color: '#35609f',
            onClick: e => {
                aquaFetch(ApplicationConf.instance.single(e.instance.id)).then(inst => {
                    const jenkinsName = inst?.parameters?.find(p => p.parameter === 'JENKINS_NAME')
                    if (jenkinsName?.value) {
                        JenkinsAction.launchService(`Deploy_${e.service.value}_${jenkinsName?.value}`)
                    } else {
                        toast.error('Paramètre manquant sur l\'instance : JENKINS_NAME')
                    }
                })
            },
        }, {
            icon: 'info',
            color: '#35609f',
            onClick: e => {
                aquaFetch(ApplicationConf.instance.single(e.instance.id)).then(inst => {
                    const instanceName = inst?.parameters?.find(p => p.parameter === 'APPLICATION_NAME')
                    const serviceName = e.service.gitlabName
                    if (instanceName?.value || serviceName?.value) {
                        window.open(`https://graylog.aquasys.fr:8081/search?q=client%3A${instanceName.value}+%26%26+"${serviceName}"&rangetype=relative&from=432000`, '_blank')
                    } else {
                        toast.error('Paramètre manquant sur l\'instance : APPLICATION_NAME')
                    }
                })
            },
        },
    ]

    const nbProblems = countBy(tableData, 'isProblem')?.true || 0
    const nbIncomplete = countBy(tableData, 'isIncomplete')?.true || 0
    const instances = uniq(results.map(r => r.instance.name))
    const services = uniq(results.map(r => r.service.name))
    const servers = uniq(results.map(r => r.instance.server))
    const branches = uniq(results.map(r => r.branch ?? 'inconnue'))

    return (
        <>
            <Grid container spacing={2} alignItems='center' style={{ paddingBottom: '5px' }}>
                <Grid size={2}>
                    <Select
                        label='Instance'
                        items={instances.map(i => ({ value: i, label: i }))}
                        value={instance}
                        onChange={v => setFilter(f => ({ ...f, instance: v }))}
                        undefinedValue
                    />
                </Grid>
                <Grid size={2}>
                    <Select
                        label='Service'
                        items={services.map(i => ({ value: i, label: i }))}
                        value={service}
                        onChange={v => setFilter(f => ({ ...f, service: v }))}
                        undefinedValue
                    />
                </Grid>
                <Grid size={2}>
                    <Select
                        label='Serveur'
                        items={servers.map(i => ({ value: i, label: i }))}
                        value={datacenter}
                        onChange={v => setFilter(f => ({ ...f, datacenter: v }))}
                        undefinedValue
                    />
                </Grid>
                <Grid size={2}>
                    <Select
                        label='Branche'
                        items={branches.map(i => ({ value: i, label: i }))}
                        value={branch}
                        onChange={v => setFilter(f => ({ ...f, branch: v }))}
                        undefinedValue
                    />
                </Grid>
                <Grid size={2}>
                    <Input
                        value={version}
                        onChange={v => setFilter(f => ({ ...f, version: v }))}
                        label='Version'
                        endAdornment={
                            <IconButton
                                onClick={() => setFilter(f => ({ ...f, versionIsEqual: !f.versionIsEqual }))}
                                edge='end'
                            >
                                {versionIsEqual ? <EqualIcon /> : <NotEqualIcon />}
                            </IconButton>
                        }
                    />
                </Grid>
                <Grid size={2}>
                    <Button
                        variant='contained'
                        fullWidth
                        onClick={() => {
                            setFilter({})
                        }}
                    >
                        Annuler
                    </Button>
                </Grid>
            </Grid>
            <CardTable
                title={(
                    <div style={{ fontSize: '1.3rem' }}>
                        {filteredByService.length} MS,&nbsp;
                        {instances.length} instances,&nbsp;
                        {servers.length} serveurs,&nbsp;
                        <span style={{ color: 'red' }}>{nbProblems} Problèmes</span>,&nbsp;
                        <span style={{ color: 'orange' }}>{nbIncomplete} Incomplets</span>
                    </div>
                )}
                color='#161832'
                hideNbElements
                lineActions={lineActions}
                rows={tableData}
                headers={TABLE_HEADERS}
                defaultSort={ { column: 'service', direction: SORT.ASC } }
                rowsPerPageDefault={50}
            />
        </>
    )
}

KwakeUpTable.propTypes = {
    results: PropTypes.arrayOf(PropTypes.object),
    filter: PropTypes.shape({
        datacenter: PropTypes.number,
        branch: PropTypes.string,
        instance: PropTypes.string,
        service: PropTypes.string,
    }),
    setFilter: PropTypes.func,
}

export default KwakeUpTable